import * as React from "react";
import { Search } from "lucide-react";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { ContextFileViewer } from "@/components/feature/layout/left-sidebar/contextFileViewer";
import { Toggle } from "@/components/feature/layout/toggle";
import { FileItem, Item } from "@/types/fse";
import { FlattenedItem } from "@/modules/chat/chat-ui.types";
import { useGetFileDetailsQuery } from "@/api/files";
import { flatFileSystem, getFilesInFolder, getParentLocations } from "@/components/feature/layout/left-sidebar/helpers";
import { useAskFile } from "@/modules/chat/hooks/useAskFile";
import { useSet } from "@/hooks/use-set";

interface Props {
  systemId: string;
  onFileSelect?: (file: FileItem, isSelected: boolean) => void;
}

export const SidebarFiles: React.FC<Props> = React.memo(function SidebarFiles({ systemId, onFileSelect }) {
  const {
    setCurrentPdfId,
    setSelectedFiles: contextSetSelectedFiles,
    setActiveTab,
    setRightDrawerContent,
    setIsRightDrawerVisible,
  } = useCurrentPdf();
  const handleAskFile = useAskFile();
  const [flattenedItems, setFlattenedItems] = React.useState<FlattenedItem[]>([]);
  const [searchQuery, setSearchQuery] = React.useState("");

  const expandedFoldersStorage = useSet();
  const selectedFilesStorage = useSet();

  const {
    data: rootItem,
    error,
    isLoading,
    refetch: loadFileSystem,
  } = useGetFileDetailsQuery({ fse_id: systemId, depth: 50 });

  const initializedRef = React.useRef(false);

  // Sync selected files to context
  const selectedFilesArray = selectedFilesStorage.array;
  const selectedFilesKey = JSON.stringify(selectedFilesArray);
  React.useEffect(() => {
    contextSetSelectedFiles(selectedFilesArray);
    // oxlint-disable-next-line react/exhaustive-deps
  }, [selectedFilesKey, contextSetSelectedFiles]);

  // Initialize on first load: expand all folders and select all files
  const expandedFoldersSet = expandedFoldersStorage.set;
  const setExpandedFolders = expandedFoldersStorage.setValues;
  const setSelectedFilesStorage = selectedFilesStorage.setValues;
  React.useEffect(() => {
    if (rootItem && !initializedRef.current) {
      // Collect all folder IDs for expansion
      const allFolderIds: string[] = [];
      const allFileIds: string[] = [];
      const collectIds = (item: Item) => {
        if (item.type === "folder") {
          allFolderIds.push(item.id);
          Object.values(item.items).forEach(collectIds);
        } else if (!item.rejection_reason) {
          allFileIds.push(item.id);
        }
      };
      collectIds(rootItem);

      // Set both states together
      setExpandedFolders(allFolderIds);
      if (allFileIds.length > 0) {
        setSelectedFilesStorage(allFileIds);
      }
      initializedRef.current = true;
    }
  }, [rootItem, setExpandedFolders, setSelectedFilesStorage]);

  // Flatten file system when expanded folders change
  const expandedFoldersKey = JSON.stringify(Array.from(expandedFoldersSet));
  React.useEffect(() => {
    if (rootItem) {
      const rows = flatFileSystem(rootItem, 0, expandedFoldersSet, true);
      setFlattenedItems(rows);
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [rootItem, expandedFoldersKey]);

  const toggleFolder = React.useCallback(
    (folderId: string) => {
      expandedFoldersStorage.toggle(folderId);
    },
    [expandedFoldersStorage],
  );

  const handleFileSelect = React.useCallback(
    (file: FileItem, isSelected: boolean) => {
      onFileSelect?.(file, isSelected);
      if (isSelected) {
        selectedFilesStorage.add(file.id);
      } else {
        selectedFilesStorage.remove(file.id);
      }
    },
    [onFileSelect, selectedFilesStorage],
  );

  const handleFileClick = React.useCallback(
    (file: FileItem) => {
      setCurrentPdfId(file.id);
      setRightDrawerContent("pdf");
      setActiveTab("pdf");
      setIsRightDrawerVisible(true);
    },
    [setCurrentPdfId, setRightDrawerContent, setActiveTab, setIsRightDrawerVisible],
  );

  const fileItems = flattenedItems.filter(item => item.type === "file");
  const selectableFiles = fileItems.filter(item => !(item as FileItem).rejection_reason);
  const isSingleFile = fileItems.length === 1;
  const isAllSelected =
    selectableFiles.length > 0 && selectableFiles.every(item => selectedFilesStorage.set.has(item.id));

  const filteredItems = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return flattenedItems;
    }
    const query = searchQuery.toLowerCase();
    const matchingFileIds = new Set<string>();
    const matchingFolderIds = new Set<string>();

    flattenedItems.forEach(item => {
      if (item.type === "file") {
        const fileItem = item as FlattenedItem & FileItem;
        const nameMatch = fileItem.name.toLowerCase().includes(query);
        const summaryMatch =
          fileItem.summary?.toLowerCase().includes(query) || fileItem.small_summary?.toLowerCase().includes(query);
        if (nameMatch || summaryMatch) {
          matchingFileIds.add(item.id);
        }
      }
    });

    flattenedItems.forEach(item => {
      if (item.type === "folder") {
        const folderFiles = getFilesInFolder(item.id, rootItem!, { skipRejected: true });
        if (folderFiles.some(fileId => matchingFileIds.has(fileId))) {
          matchingFolderIds.add(item.id);
        }
      }
    });

    return flattenedItems.filter(item => {
      if (item.type === "file") {
        return matchingFileIds.has(item.id);
      }
      return matchingFolderIds.has(item.id);
    });
  }, [flattenedItems, searchQuery, rootItem]);

  const handleToggleSelect = (enabled: boolean) => {
    const fileIds = flattenedItems
      .filter(item => item.type === "file" && !(item as FileItem).rejection_reason)
      .map(item => item.id);
    selectedFilesStorage.updateMany(fileIds, enabled);
  };

  const getFolderSelectionState = React.useCallback(
    (folderId: string): "all" | "some" | "none" => {
      if (!rootItem) return "none";

      const filesInFolder = getFilesInFolder(folderId, rootItem, { skipRejected: true });
      const selectedFilesInFolder = filesInFolder.filter(fileId => selectedFilesStorage.set.has(fileId));

      if (selectedFilesInFolder.length === 0) return "none";
      if (selectedFilesInFolder.length === filesInFolder.length) return "all";
      return "some";
    },
    [rootItem, selectedFilesStorage.set],
  );

  const handleFolderToggle = React.useCallback(
    (folderId: string) => {
      if (!rootItem) return;

      const filesInFolder = getFilesInFolder(folderId, rootItem, { skipRejected: true });
      const currentState = getFolderSelectionState(folderId);
      const shouldSelect = currentState !== "all";

      selectedFilesStorage.updateMany(filesInFolder, shouldSelect);
    },
    [rootItem, getFolderSelectionState, selectedFilesStorage],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="p-4">
        <div className="flex items-center">
          <h2 className="text-lg font-bold">{isSingleFile ? "File" : "Select files"}</h2>
          {!isSingleFile && (
            <div className="ml-auto flex-shrink-0">
              <Toggle enabled={isAllSelected} size="xs" onChange={handleToggleSelect} />
            </div>
          )}
        </div>
        {!isSingleFile && (
          <div className="relative mt-3">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 py-1.5 pl-8 pr-3 text-sm placeholder:text-gray-400 focus:border-gray-400 focus:outline-none"
            />
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-24">
        <ContextFileViewer
          loading={isLoading}
          error={error}
          rootItem={rootItem}
          flattenedItems={filteredItems}
          loadFileSystem={loadFileSystem}
          showFolders={true}
          toggleFolder={toggleFolder}
          handleFileClick={handleFileClick}
          handleFileSelect={handleFileSelect}
          handleAskFile={handleAskFile}
          selectedFiles={selectedFilesStorage.set}
          getParentLocations={getParentLocations}
          handleFolderToggle={handleFolderToggle}
          getFolderSelectionState={getFolderSelectionState}
        />
      </div>
    </div>
  );
});
